import {useEffect, useState} from 'react'
import OtpInput, {formatOtpTimer} from '@/customComponents/uiComponents/global/OtpInput.jsx'
import {BOOKING_CONSTRAINTS} from '@/constants/booking'
import {accountDeletionService, DELETION_CONFIRMATION} from '@/services/accountDeletionService'

/**
 * DELETE MY ACCOUNT — the whole flow, from the warning to the reference number.
 *
 * ── ⚠️ ONE COMPONENT FOR THE DASHBOARD AND THE PUBLIC PAGE ────────────────────────
 * The endpoints take no session, so the signed-in flow and the signed-out one Google Play
 * requires are the same three screens with the same copy. Two implementations would drift,
 * and the one that drifted would be the public page nobody on the team ever opens.
 *
 * The differences are props: who is signed in (`defaultContact`), which leg, and what to do
 * afterwards (`onDeleted` — sign out in the app, nothing on the public page).
 *
 * ── ⚠️ THREE DELIBERATE OBSTACLES, AND NONE OF THEM IS THEATRE ────────────────────
 * A warning to read, a code that proves the contact, and the word DELETE typed out. This is
 * the one action in the product that support cannot undo — not "hard to undo", genuinely
 * impossible, including for an admin. The friction is the feature.
 *
 * ── ⚠️ THE COPY IS THE BACKEND TEAM'S, WORD FOR WORD ──────────────────────────────
 * It tells the user what actually happens: rides cancelled free, credit forfeited, records
 * kept for tax and legal. Rewriting any of it to sound softer would make a legal notice
 * into a marketing one.
 */

const STEP = {warn: 'warn', code: 'code', done: 'done'}
const OTP_LENGTH = 6
const emptyOtp = () => Array(OTP_LENGTH).fill('')

export default function DeleteAccountFlow({
    leg,
    /** Prefilled and locked when we already know who this is — see `contactLocked`. */
    defaultContact = '',
    /** Called after a successful deletion. The app signs out here; the public page does not. */
    onDeleted,
    /** Leave the flow without deleting. */
    onCancel,
}) {
    const [step, setStep] = useState(STEP.warn)
    const [contact, setContact] = useState(defaultContact)
    // Six boxes, like every other code screen (login, register, profile verification).
    const [otp, setOtp] = useState(emptyOtp)
    const otpCode = otp.join('')
    // Seconds until "Send a new code" unlocks — the same cooldown the login OTP uses.
    const [resendTimer, setResendTimer] = useState(0)
    const [confirmWord, setConfirmWord] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const [resendable, setResendable] = useState(false)
    const [notice, setNotice] = useState('')
    const [reference, setReference] = useState(null)

    /* ⚠️ LOCKED ONLY WHEN WE WERE GIVEN ONE. Signed in, the contact is the account's own
       and letting it be edited would let someone request a code for a DIFFERENT account
       from inside their own settings. Signed out, it is the only thing we have. */
    const contactLocked = !!defaultContact

    useEffect(() => {
        if (resendTimer <= 0) return undefined
        const timer = setInterval(() => setResendTimer((prev) => Math.max(prev - 1, 0)), 1000)
        return () => clearInterval(timer)
    }, [resendTimer])
    // The server can also say "resend now" (e.g. the code's attempts ran out) — that wins
    // over the cooldown.
    const canResend = resendTimer <= 0 || resendable

    const sendCode = async () => {
        const value = contact.trim()
        if (!value || busy) return

        setBusy(true)
        setError('')
        const result = await accountDeletionService.requestCode(leg, value)
        setBusy(false)

        if (!result.success) {
            setError(result.error)
            setResendable(!!result.resendable)
            return
        }
        /* ⚠️ THE SERVER'S OWN SENTENCE, unchanged. It is careful not to say whether an
           account was found, and rewording it is how that protection gets lost. */
        setNotice(result.message)
        /* ⚠️ TEST ACCOUNTS ONLY — the server decides, and a real account never returns a code.
           It is typed into the boxes for the reviewer, so an App Store reviewer can complete the
           flow without a phone that can receive our SMS. */
        const testDigits = String(result.testOtp ?? '').replace(/\D/g, '').slice(0, OTP_LENGTH).split('')
        setOtp(testDigits.length === OTP_LENGTH ? testDigits : emptyOtp())
        setResendable(false)
        setResendTimer(BOOKING_CONSTRAINTS.OTP_RESEND_TIMER_SECONDS)
        setStep(STEP.code)
    }

    const confirm = async () => {
        if (busy) return
        /* Belt and braces with the disabled button: the server rejects anything but the
           literal word, and failing here costs nothing where failing there burns an
           attempt against the five-try limit. */
        if (confirmWord.trim().toUpperCase() !== DELETION_CONFIRMATION) return

        setBusy(true)
        setError('')
        const result = await accountDeletionService.confirm(leg, contact.trim(), otpCode)
        setBusy(false)

        if (!result.success) {
            setError(result.error)
            setResendable(!!result.resendable)
            return
        }
        setReference(result.reference)
        setStep(STEP.done)
    }

    if (step === STEP.done) {
        return (
            <div className="flex flex-col gap-4">
                <h4 className="mb-0!">Your account has been deleted</h4>
                <p className="mb-0! text-(--text-secondary)">
                    You have been signed out. Your deletion reference is{' '}
                    <strong className="text-(--text-primary)">{reference || 'not available'}</strong>.
                    The old account cannot be recovered. Limited records may be retained for
                    transaction, tax, safety, dispute or legal requirements. A future sign-up
                    will create a new account.
                </p>
                {/* ⚠️ NO "UNDO", NO SUPPORT LINK, NO NEXT STEP. There is nothing anyone can
                    do from here, and offering a route implies otherwise. */}
                {onDeleted && (
                    <button
                        type="button"
                        onClick={onDeleted}
                        className="min-h-14 w-full cursor-pointer rounded-full border-0 bg-(--text-primary) px-6 font-semibold text-(--bg-white)"
                    >
                        Done
                    </button>
                )}
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-5">
            {step === STEP.warn && (
                <>
                    <h4 className="mb-0!">Delete your account?</h4>
                    <p className="mb-0! text-(--text-secondary)">
                        Your account will be deleted and you will be signed out. This cannot be
                        undone, and the account cannot be recovered. Any upcoming ride will be
                        cancelled free of charge; any unused credit is forfeited. Hopn may retain
                        limited transaction, tax, safety, dispute or legal records for required
                        periods. If you sign up again, a new account will be created.
                    </p>

                    {!contactLocked && (
                        <label className="flex flex-col gap-2">
                            <span className="font-medium text-(--text-primary)">Email or phone number</span>
                            <input
                                type="text"
                                value={contact}
                                onChange={(e) => setContact(e.target.value)}
                                placeholder="you@example.com"
                                autoComplete="off"
                                className="w-full rounded-(--radius-lg) border border-(--border-color) bg-(--bg-white) px-4 py-3 outline-none"
                            />
                        </label>
                    )}

                    {error && <p className="mb-0! text-sm text-(--text-red)" role="alert">{error}</p>}

                    {/* ⚠️ KEEP ON THE LEFT, DESTRUCTIVE ON THE RIGHT — one row at every width, and
                        "Keep Account" is the one that looks like the button. The dangerous
                        action should never be the one a thumb lands on by habit. */}
                    <div className="flex gap-3">
                        {onCancel && (
                            <button
                                type="button"
                                onClick={onCancel}
                                className="min-h-14 flex-1 cursor-pointer rounded-full border-0 bg-(--bg-grey) px-6 font-semibold text-(--text-primary)"
                            >
                                Keep Account
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={sendCode}
                            disabled={busy || !contact.trim()}
                            className="min-h-14 flex-1 cursor-pointer rounded-full border-0 bg-(--text-red) px-6 font-semibold text-white disabled:cursor-default disabled:opacity-40"
                        >
                            {busy ? 'Sending…' : 'Delete Account'}
                        </button>
                    </div>
                </>
            )}

            {step === STEP.code && (
                <>
                    <h4 className="mb-0!">Enter the verification code</h4>
                    <p className="mb-0! text-(--text-secondary)">{notice}</p>

                    {/* The shared six-box input: paste, backspace walk-back, numeric keypad. */}
                    <OtpInput value={otp} onChange={setOtp} autoFocus className="my-1"/>

                    {/* ⚠️ UNDER THE BOXES, WITH ITS CLOCK — same as the login and profile code
                        screens. Locked until the cooldown ends (or the server says resend now):
                        an SMS that never arrives produces no error to react to, so the way out
                        has to be there, but not spammable. */}
                    <div className="flex items-center justify-center gap-3">
                        <button
                            type="button"
                            onClick={sendCode}
                            disabled={!canResend || busy}
                            className={`text-sm font-medium border-0 bg-transparent p-0 ${
                                canResend && !busy ? 'cursor-pointer text-(--text-primary) hover:opacity-80' : 'cursor-not-allowed text-gray-400'
                            }`}
                        >
                            Send a new code
                        </button>
                        {resendTimer > 0 && !resendable && (
                            <span className="text-sm text-(--text-secondary)">{formatOtpTimer(resendTimer)}</span>
                        )}
                    </div>

                    <label className="flex flex-col gap-2">
                        <span className="font-medium text-(--text-primary)">
                            Type {DELETION_CONFIRMATION} to confirm
                        </span>
                        <input
                            type="text"
                            value={confirmWord}
                            onChange={(e) => setConfirmWord(e.target.value)}
                            placeholder={DELETION_CONFIRMATION}
                            autoComplete="off"
                            className="w-full rounded-(--radius-lg) border border-(--border-color) bg-(--bg-white) px-4 py-3 outline-none"
                        />
                    </label>

                    {error && <p className="mb-0! text-sm text-(--text-red)" role="alert">{error}</p>}

                    {/* Keep on the left, Delete on the right — same row as the first screen. */}
                    <div className="flex gap-3">
                        {onCancel && (
                            <button
                                type="button"
                                onClick={onCancel}
                                className="min-h-14 flex-1 cursor-pointer rounded-full border-0 bg-(--bg-grey) px-6 font-semibold text-(--text-primary)"
                            >
                                Keep Account
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={confirm}
                            disabled={
                                busy
                                || otpCode.length !== OTP_LENGTH
                                || confirmWord.trim().toUpperCase() !== DELETION_CONFIRMATION
                            }
                            className="min-h-14 flex-1 cursor-pointer rounded-full border-0 bg-(--text-red) px-6 font-semibold text-white disabled:cursor-default disabled:opacity-40"
                        >
                            {busy ? 'Deleting…' : 'Delete Account'}
                        </button>
                    </div>
                </>
            )}
        </div>
    )
}

export {DeleteAccountFlow}
